import { gql } from '@/__generated__/gql';
import { StrategyDetailsPageDetailsSectionInvestmentQuery } from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { BurgerMenuIcon } from '@/assets/static/icons/burger-menu';
import { CloseMenuIcon } from '@/assets/static/icons/close-menu';
import Tabs, { ITab, TabMobileWrap, TabTitle, TabsControl } from '@/components/basicComponents/tabs';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

export const STRATEGY_DETAILS_PAGE_DETAILS_SECTION_INVESTMENT_QUERY = gql(`
  query StrategyDetailsPageDetailsSectionInvestment($id: String!) {
    Investment(id: $id) {
      id
      name
      type
      custodied
      assetClass {
        id
        name
      }
      subAssetClass {
        id
        name
      }
      pacingType
      pacingStartingYear
      committedCapital
      capitalCalled
      unfundedCommitments
      distributionsToDate
      NAV
      sectors
      investorDocsDue
      minimum
      ubtiBlocker
      subAssetClasses
    }
  }
`);

interface DetailsProps {
  investmentId: string;
  tabs?: ITab[];
  activeTab?: string;
  isTabMenuOpen?: boolean;
  summary?: string;
  showMinimalData: boolean;
  showGreyTabs: boolean;
  showInfoTags: boolean;
  tabsPlacement: 'top' | 'bottom';
  constrainWidth?: boolean;
  setActiveTab?: (value: string) => void;
  setIsTabMenuOpen?: Dispatch<SetStateAction<boolean>>;
}

export const Details = ({
  investmentId,
  tabs,
  activeTab,
  isTabMenuOpen,
  summary,
  showMinimalData,
  showGreyTabs,
  showInfoTags,
  tabsPlacement,
  constrainWidth = false,
  setActiveTab,
  setIsTabMenuOpen
}: DetailsProps) => {
  const { isMobile, isDesktop, isTablet, isLargeDesktop } = useResponsive();
  const { user } = useAuth();

  const [investmentData, setInvestmentData] = useState<StrategyDetailsPageDetailsSectionInvestmentQuery['Investment'] | null>(null);

  useQuery(STRATEGY_DETAILS_PAGE_DETAILS_SECTION_INVESTMENT_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !investmentId,
    variables: { id: investmentId },
    onCompleted: (data) => {
      setInvestmentData(data.Investment);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const greyTabsData: { name: string; showItem: boolean; value: JSX.Element }[] = useMemo(() => {
    if (!investmentData) {
      return [];
    }
    const { committedCapital, capitalCalled, unfundedCommitments, distributionsToDate, NAV } = investmentData;

    return [
      {
        name: 'Committed Capital',
        showItem: true,
        value: <FormattingTooltip>{committedCapital}</FormattingTooltip>
      },
      {
        name: 'Capital Called',
        showItem: true,
        value: <FormattingTooltip>{capitalCalled}</FormattingTooltip>
      },
      {
        name: 'Unfunded Commitment',
        showItem: true,
        value: <FormattingTooltip>{unfundedCommitments}</FormattingTooltip>
      },
      {
        name: 'Current NAV',
        showItem: true,
        value: <FormattingTooltip>{NAV}</FormattingTooltip>
      },
      {
        name: 'Distributions to Date',
        showItem: true,
        value: <FormattingTooltip>{distributionsToDate}</FormattingTooltip>
      }
    ];
  }, [investmentData]);

  const infoTagsData: { name: string; showItem: boolean; value: JSX.Element }[] = useMemo(() => {
    if (!investmentData) {
      return [];
    }
    const { minimum, ubtiBlocker, investorDocsDue, custodied, type } = investmentData;

    return [
      {
        name: 'Minmum',
        showItem: true,
        value: <FormattingTooltip>{minimum}</FormattingTooltip>
      },
      {
        name: 'Tax Exempt Suitable',
        showItem: true,
        value: <>{ubtiBlocker ? 'Yes' : 'No'}</>
      },
      {
        name: 'Investor docs due',
        showItem: !!investorDocsDue,
        value: <>{getUTCDate(new Date(investorDocsDue))}</>
      },
      {
        name: 'Custodian',
        showItem: !custodied,
        value: <>No</>
      },
      {
        name: 'Type',
        showItem: true,
        value: <>{type}</>
      }
    ];
  }, [investmentData]);

  if (!investmentData) return <></>;

  const assetClassValue = investmentData.subAssetClass
    ? `${investmentData.assetClass?.name} - ${investmentData.subAssetClass?.name}`
    : investmentData.assetClass?.name;
  const showSectors = !!investmentData.subAssetClasses.filter((sector) => sector !== '').length;

  return (
    <DetailsWrapper showMinimalData={user && user.tenant.type === TenantType.ADVISORY_FIRM && showMinimalData} isMobile={isMobile}>
      {tabs && activeTab && setActiveTab && setIsTabMenuOpen && tabsPlacement === 'top' && (
        <>
          {(isDesktop || isTablet || isLargeDesktop) && (
            <CustomTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} tabsPlacement="top" />
          )}
        </>
      )}
      <DetailsContainer isMobile={isMobile}>
        {showGreyTabs && (
          <CardsWrapper isMobile={isMobile}>
            {greyTabsData.map(
              ({ name, showItem, value }) =>
                showItem && (
                  <Card key={name} isMobile={isMobile}>
                    <CardValue>{value}</CardValue>
                    <CardTitle>{name}</CardTitle>
                  </Card>
                )
            )}
          </CardsWrapper>
        )}
        <DetailsBoby>
          <DetailsRow>
            {summary && (
              <SummaryWrapper>
                <SummaryText fontWeight={700}>
                  Summary: <SummaryText fontWeight={400}>{summary}</SummaryText>
                </SummaryText>
              </SummaryWrapper>
            )}
            <DetailsItems isMobile={isMobile} constrainWidth={constrainWidth} withBorderBottom={showInfoTags}>
              {!!investmentData.assetClass && (
                <DetailsItem>
                  <ApprovedIconWrapper>
                    <ApprovedIcon width={23} height={23} fill={themeVar('font.weak')} />
                  </ApprovedIconWrapper>
                  <ItemKey>Asset Class:</ItemKey>
                  {assetClassValue}
                </DetailsItem>
              )}
              {!!investmentData.pacingType && (
                <DetailsItem>
                  <ApprovedIconWrapper>
                    <ApprovedIcon width={23} height={23} fill={themeVar('font.weak')} />
                  </ApprovedIconWrapper>
                  <ItemKey>Vintage:</ItemKey>
                  {investmentData.pacingType === 'EVERGREEN' && 'Evergreen'}
                  {investmentData.pacingType === 'DRAWDOWN' && (investmentData.pacingStartingYear ? investmentData.pacingStartingYear : 'Drawdown')}
                </DetailsItem>
              )}
              {showSectors && (
                <DetailsItem>
                  <SectorsContainer>
                    <ApprovedIconWrapper>
                      <ApprovedIcon width={23} height={23} fill={themeVar('font.weak')} />
                    </ApprovedIconWrapper>
                    <ItemKey>Sectors:</ItemKey>
                    {investmentData?.subAssetClasses?.map((sector, index) => (
                      <div key={`${sector}-${index}`}>
                        {sector.trim()}
                        {index < investmentData?.subAssetClasses.length - 1 && ','}
                      </div>
                    ))}
                  </SectorsContainer>
                </DetailsItem>
              )}
            </DetailsItems>
          </DetailsRow>
          {showInfoTags && (
            <DetailsRow>
              <InfoTagsItems constrainWidth={constrainWidth}>
                {infoTagsData.map(
                  ({ name, showItem, value }) =>
                    showItem && (
                      <DetailsItem key={name}>
                        <GreyTabItem>
                          <InfoIcon width={12} height={12} fill={themeVar('font.weak')} />
                          <ItemKey>{name}:</ItemKey>
                          {value}
                        </GreyTabItem>
                      </DetailsItem>
                    )
                )}
              </InfoTagsItems>
            </DetailsRow>
          )}
        </DetailsBoby>
      </DetailsContainer>
      {tabs && activeTab && setActiveTab && setIsTabMenuOpen && tabsPlacement === 'bottom' && (
        <>
          {(isDesktop || isTablet || isLargeDesktop) && (
            <CustomTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} tabsPlacement="bottom" />
          )}
        </>
      )}
      {tabs && activeTab && setActiveTab && setIsTabMenuOpen && isMobile && (
        <MobileTabsWrapper isMobile={isMobile}>
          {tabs.length !== 1 && (
            <>
              {isTabMenuOpen ? (
                <CloseMenuIcon fill={themeVar('action.primary')} onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} />
              ) : (
                <BurgerMenuIcon fill={themeVar('action.primary')} onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} />
              )}
            </>
          )}
          <TabMobileWrap onClick={() => setActiveTab(activeTab)} isActive>
            <TabTitle isActive isMobile={isMobile}>
              {activeTab}
            </TabTitle>
          </TabMobileWrap>
        </MobileTabsWrapper>
      )}
    </DetailsWrapper>
  );
};

const DetailsWrapper = styled.div<{ isMobile: boolean; showMinimalData: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  background-color: ${({ showMinimalData }) => (showMinimalData ? themeVar('layer.base') : themeVar('layer.1'))};
  transition: 1s;

  ${({ showMinimalData }) =>
    !showMinimalData
      ? css`
          border-bottom: 1px solid ${themeVar('border.base')};
        `
      : ''}
`;

const CustomTabs = styled(Tabs)<{ tabsPlacement: 'top' | 'bottom' }>`
  padding: 0 60px;

  ${({ tabsPlacement }) =>
    tabsPlacement === 'top'
      ? css`
          border-bottom: 1px solid ${themeVar('border.base')};
        `
      : css`
          & > div {
            margin-top: 0;
          }
        `}
`;

const MobileTabsWrapper = styled(TabsControl)`
  padding: 10px 24px;
`;

const DetailsContainer = styled.div<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '10px 40px' : '10px 60px')};
`;

const DetailsBoby = styled.div`
  color: ${themeVar('font.base')};
  width: 100%;
  padding: 10px 0px;
`;

const DetailsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 15px;
`;

const SummaryWrapper = styled.div`
  padding: 5px 10px;
`;

const SummaryText = styled.span<{ fontWeight: number }>`
  font-size: 16px;
  font-weight: ${({ fontWeight }) => fontWeight};
  line-height: 22px;
  overflow-wrap: anywhere;
`;

const DetailsItems = styled.div<{ isMobile: boolean; constrainWidth: boolean; withBorderBottom?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
  row-gap: 10px;
  padding: 0 10px ${({ withBorderBottom }) => (withBorderBottom ? '15px' : '10px')} 10px;
  border-bottom: ${({ withBorderBottom }) => (withBorderBottom ? `1px solid ${themeVar('border.base')}` : 'none')};

  ${({ constrainWidth }) =>
    constrainWidth &&
    css`
      max-width: 1000px;
      width: 100%;
    `}
`;

const DetailsItem = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  color: ${themeVar('font.strong')};
`;

const ItemKey = styled.span`
  font-weight: 400;
  color: ${themeVar('font.weak')};
`;

const ApprovedIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectorsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const GreyTabItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  padding: 3px 10px;
  background: ${themeVar('layer.12')};
  font-size: 13px;
  font-weight: 600;
  line-height: 18.2px;
  color: ${themeVar('font.base')};
`;

const CardsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 10px;
  padding-bottom: 10px;
`;

const Card = styled.div<{ isMobile: boolean }>`
  display: flex;
  min-width: 80px;
  width: 100%;
  height: 80px;
  min-height: 80px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${themeVar('layer.base')};
  border-radius: 10px;
`;

const CardTitle = styled.div`
  color: ${themeVar('font.weak')};
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  text-transform: uppercase;
`;

const CardValue = styled.div`
  color: ${themeVar('font.strong')};
  font-size: 23px;
  font-weight: 700;
  line-height: 32.3px;
`;

const InfoTagsItems = styled.div<{ constrainWidth: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 0 10px;

  ${({ constrainWidth }) =>
    constrainWidth &&
    css`
      max-width: 1000px;
      width: 100%;
    `}
`;
